import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, CheckCircle2, Trash2, Plus, Pencil } from "lucide-react";
import api, { API } from "../lib/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const token = localStorage.getItem("sk_admin_token");

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    refresh();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    try {
      const [b, c, p] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/admin/blog"),
      ]);
      setBookings(b.data); setContacts(c.data); setPosts(p.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("sk_admin_token");
        navigate("/admin/login");
      }
    }
  };

  const logout = () => { localStorage.removeItem("sk_admin_token"); navigate("/admin/login"); };

  const markContacted = async (kind, id) => {
    const url = kind === "booking" ? `/admin/bookings/${id}` : `/admin/contacts/${id}`;
    await api.patch(url, { status: "contacted" });
    toast.success("Marked as contacted");
    refresh();
  };

  const exportXlsx = async () => {
    const res = await fetch(`${API}/admin/export/bookings`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "bookings.xlsx"; a.click(); URL.revokeObjectURL(url);
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Post deleted");
    refresh();
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }} data-testid="admin-dashboard">
      <header className="bg-white border-b">
        <div className="container-soft py-4 flex items-center justify-between">
          <div className="font-display text-2xl">Admin · Sudiksha Kulharia</div>
          <button onClick={logout} className="text-sm flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: "var(--accent-beige)" }} data-testid="admin-logout">
            <LogOut size={14}/> Logout
          </button>
        </div>
      </header>

      <div className="container-soft py-6 flex gap-2 flex-wrap">
        {[["bookings", `Bookings (${bookings.length})`], ["contacts", `Contacts (${contacts.length})`], ["blog", `Blog (${posts.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-5 py-2 rounded-full text-sm transition-colors ${tab === k ? "text-white" : ""}`} style={{ background: tab === k ? "var(--primary)" : "white", border: "1px solid var(--border-soft)", color: tab === k ? "#fff" : "var(--text-primary)" }} data-testid={`admin-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="container-soft pb-20">
        {tab === "bookings" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={exportXlsx} className="text-sm flex items-center gap-2 px-5 py-2.5 rounded-full text-white" style={{ background: "var(--primary)" }} data-testid="admin-export-btn">
                <Download size={14}/> Export to Excel
              </button>
            </div>
            <DataTable
              rows={bookings}
              cols={["created_at", "full_name", "email", "phone", "city", "mode", "preferred_date", "preferred_time", "status", "actions"]}
              labels={{ created_at: "Created", full_name: "Name", preferred_date: "Date", preferred_time: "Time" }}
              actions={(r) => r.status === "new" && (
                <button onClick={() => markContacted("booking", r.id)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full" style={{ background: "var(--accent-blue)" }} data-testid={`mark-${r.id}`}>
                  <CheckCircle2 size={12}/> Mark contacted
                </button>
              )}
              testIdPrefix="booking"
            />
            <DetailList rows={bookings} field="reason" label="Reason"/>
          </div>
        )}

        {tab === "contacts" && (
          <DataTable
            rows={contacts}
            cols={["created_at", "name", "email", "phone", "message", "status", "actions"]}
            labels={{ created_at: "Created" }}
            actions={(r) => r.status === "new" && (
              <button onClick={() => markContacted("contact", r.id)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full" style={{ background: "var(--accent-blue)" }} data-testid={`mark-c-${r.id}`}>
                <CheckCircle2 size={12}/> Mark contacted
              </button>
            )}
            testIdPrefix="contact"
          />
        )}

        {tab === "blog" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setEditing({ title: "", excerpt: "", content: "", cover_image: "", category: "Mental Health", published: true })} className="text-sm flex items-center gap-2 px-5 py-2.5 rounded-full text-white" style={{ background: "var(--primary)" }} data-testid="admin-new-post">
                <Plus size={14}/> New Post
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {posts.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl p-5 border" data-testid={`admin-post-${p.id}`}>
                  <div className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--primary-deep)" }}>{p.category}</div>
                  <h3 className="font-display text-xl mb-2">{p.title}</h3>
                  <p className="text-sm mb-3 line-clamp-2" style={{ color: "var(--text-secondary)" }}>{p.excerpt}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(p)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full" style={{ background: "var(--accent-beige)" }}><Pencil size={12}/> Edit</button>
                    <button onClick={() => deletePost(p.id)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full" style={{ background: "#FECACA" }}><Trash2 size={12}/> Delete</button>
                  </div>
                </div>
              ))}
            </div>
            {editing && <BlogEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }}/>}
          </div>
        )}
      </div>
    </div>
  );
}

function DataTable({ rows, cols, labels = {}, actions, testIdPrefix }) {
  if (rows.length === 0) return <div className="py-20 text-center text-sm" style={{ color: "var(--text-secondary)" }}>No records yet.</div>;
  return (
    <div className="bg-white rounded-2xl border overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--bg-secondary)" }}>
            <tr>
              {cols.map((c) => <th key={c} className="text-left p-3 font-medium" style={{ color: "var(--text-secondary)" }}>{labels[c] || c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`${testIdPrefix}-row-${r.id}`}>
                {cols.map((c) => (
                  <td key={c} className="p-3 align-top max-w-xs">
                    {c === "actions" ? actions(r) :
                     c === "status" ? <span className={`text-xs px-2 py-1 rounded-full ${r.status === 'contacted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span> :
                     c === "created_at" ? new Date(r[c]).toLocaleString() :
                     <span style={{ color: "var(--text-primary)" }}>{String(r[c] || "—").slice(0, 80)}</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailList({ rows, field, label }) {
  const items = rows.filter((r) => r[field]);
  if (items.length === 0) return null;
  return (
    <details className="mt-6 bg-white rounded-2xl border p-5">
      <summary className="cursor-pointer text-sm font-medium">View full {label.toLowerCase()} for each booking</summary>
      <div className="mt-4 space-y-3">
        {items.map((r) => (
          <div key={r.id} className="text-sm p-3 rounded-lg" style={{ background: "var(--bg-secondary)" }}>
            <div className="font-medium">{r.full_name} — {new Date(r.created_at).toLocaleString()}</div>
            <div style={{ color: "var(--text-secondary)" }} className="mt-1 whitespace-pre-line">{r[field]}</div>
          </div>
        ))}
      </div>
    </details>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState(post);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    if (!form.title || !form.content) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      if (post.id) {
        await api.put(`/admin/blog/${post.id}`, { title: form.title, excerpt: form.excerpt, content: form.content, cover_image: form.cover_image, category: form.category, published: form.published });
      } else {
        await api.post(`/admin/blog`, form);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="blog-editor-modal">
      <div className="bg-white rounded-3xl p-6 md:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-2xl mb-4">{post.id ? "Edit Post" : "New Post"}</h2>
        <div className="space-y-4">
          <input className="be-input" placeholder="Title" value={form.title} onChange={set("title")} data-testid="be-title"/>
          <input className="be-input" placeholder="Category (e.g., Anxiety, Self-Care)" value={form.category} onChange={set("category")}/>
          <input className="be-input" placeholder="Cover image URL (optional)" value={form.cover_image || ""} onChange={set("cover_image")}/>
          <textarea className="be-input" rows={2} placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")}/>
          <textarea className="be-input" rows={10} placeholder="Content (plain text, line breaks supported)" value={form.content} onChange={set("content")} data-testid="be-content"/>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={set("published")}/> Published</label>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="px-5 py-2.5 rounded-full text-sm" style={{ background: "var(--accent-beige)" }}>Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-full text-sm text-white disabled:opacity-60" style={{ background: "var(--primary)" }} data-testid="be-save">{saving ? "Saving..." : "Save"}</button>
          </div>
        </div>
        <style>{`.be-input{width:100%;padding:10px 14px;border-radius:12px;border:1px solid var(--border-soft);background:var(--bg-primary);font-size:14px}`}</style>
      </div>
    </div>
  );
}
